import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  Target,
  BookOpen,
  Calendar,
  Award,
  DollarSign,
  Loader2,
  AlertCircle,
  Sparkles,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Clock,
  Zap
} from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { cn } from '@shared/utils/cn';

interface SkillsGapAnalysisProps {
  jobMatchId: string;
}

interface JobMatch {
  id: string;
  job_title: string;
  company_name: string;
}

interface SkillsGapData {
  skills_gap_matrix: any[] | null;
  priority_skills_development: any[] | null;
  learning_resources: any[] | null;
  development_timeline: any | null;
  leverage_strengths: any[] | null;
  roi_summary: any | null;
  full_skills_gap_analysis: string | null;
}

export const SkillsGapAnalysis: React.FC<SkillsGapAnalysisProps> = ({ jobMatchId }) => {
  const { user } = useAuth();
  const [jobMatch, setJobMatch] = useState<JobMatch | null>(null);
  const [analysis, setAnalysis] = useState<SkillsGapData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['matrix', 'priority'])
  );

  useEffect(() => {
    loadData();
  }, [jobMatchId]);

  const loadData = async () => {
    try {
      setIsLoading(true);

      const { data: matchData, error: matchError } = await supabase
        .from('job_matches')
        .select('id, job_title, company_name')
        .eq('id', jobMatchId)
        .maybeSingle();

      if (matchError) throw matchError;
      if (!matchData) throw new Error('Job match not found');

      setJobMatch(matchData);

      const { data: analysisData, error: analysisError } = await supabase
        .from('job_analysis')
        .select('*')
        .eq('job_match_id', jobMatchId)
        .maybeSingle();

      if (analysisError && analysisError.code !== 'PGRST116') {
        console.warn('Error loading analysis:', analysisError);
      }

      if (analysisData) {
        setAnalysis(analysisData);
      }

      if (user) {
        await supabase.from('match_history').insert({
          user_id: user.id,
          job_match_id: jobMatchId,
          interaction_type: 'skills_gap_viewed',
          metadata: { source: 'skills_gap_analysis' },
        });
      }
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast.error('Failed to load skills gap analysis');
    } finally {
      setIsLoading(false);
    }
  };

  const generateAnalysis = async () => {
    if (!user) {
      toast.error('You must be logged in to generate skills gap analysis');
      return;
    }

    try {
      setIsGenerating(true);
      toast.info('Analyzing skills gap and creating development plan...');

      const { data, error } = await supabase.functions.invoke('analyze-skills-gap', {
        body: { jobMatchId, userId: user.id },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to generate analysis');

      toast.success('Skills gap analysis completed!');
      await loadData();
    } catch (error: any) {
      console.error('Error generating analysis:', error);
      toast.error(error.message || 'Failed to generate skills gap analysis');
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      newSet.has(sectionId) ? newSet.delete(sectionId) : newSet.add(sectionId);
      return newSet;
    });
  };

  const getPriorityColor = (priority: string): string => {
    const p = priority?.toLowerCase();
    if (p?.includes('high') || p?.includes('critical')) return 'bg-red-100 text-red-800 border-red-200';
    if (p?.includes('medium')) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-green-100 text-green-800 border-green-200';
  };

  const getGapSizeColor = (gap: string): string => {
    const g = gap?.toLowerCase();
    if (g?.includes('critical') || g?.includes('large')) return 'text-red-600';
    if (g?.includes('medium')) return 'text-yellow-600';
    return 'text-green-600';
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-border/40 p-6 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-gray-600">Loading skills gap analysis...</p>
      </div>
    );
  }

  if (!jobMatch) {
    return (
      <div className="bg-white rounded-xl border border-border/40 p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Job Match Not Found</h3>
      </div>
    );
  }

  const hasAnalysis = analysis?.skills_gap_matrix || analysis?.priority_skills_development;

  if (!hasAnalysis) {
    return (
      <div className="bg-white rounded-xl border border-border/40 p-6">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <TrendingUp className="h-8 w-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Generate Your Skills Gap Analysis
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Get a comprehensive analysis of the skills gap between your current competencies and this
            role's requirements, with a detailed development plan and ROI analysis.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h4 className="font-semibold text-blue-900 mb-3">Your Analysis Will Include:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <Target className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Skills Gap Matrix</p>
                <p className="text-sm text-blue-700">Current vs required competency levels</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <TrendingUp className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Priority Rankings with ROI</p>
                <p className="text-sm text-blue-700">High/Medium/Low impact skills</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <BookOpen className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Learning Resources</p>
                <p className="text-sm text-blue-700">Specific courses and certifications</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">30-60-90 Day Timeline</p>
                <p className="text-sm text-blue-700">Structured development roadmap</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Zap className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Leverage Opportunities</p>
                <p className="text-sm text-blue-700">Using BRAG achievements to accelerate</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <DollarSign className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">ROI Analysis</p>
                <p className="text-sm text-blue-700">Investment vs career impact</p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <Button onClick={generateAnalysis} disabled={isGenerating} size="lg" className="px-8">
            {isGenerating ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Analyzing Skills Gap...
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5 mr-2" />
                Generate Skills Gap Analysis
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-border/40 p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Skills Gap Analysis</h2>
            <p className="text-gray-600">
              {jobMatch.job_title} at {jobMatch.company_name}
            </p>
          </div>
          <Button onClick={generateAnalysis} disabled={isGenerating} variant="outline" size="sm">
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Regenerating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Regenerate
              </>
            )}
          </Button>
        </div>

        {/* Skills Gap Matrix */}
        {analysis.skills_gap_matrix && analysis.skills_gap_matrix.length > 0 && (
          <div className="mb-6">
            <button
              onClick={() => toggleSection('matrix')}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors mb-4"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg text-white">
                  <Target className="h-5 w-5" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">Skills Gap Matrix</h3>
                  <p className="text-sm text-gray-600">
                    {analysis.skills_gap_matrix.length} skills analyzed
                  </p>
                </div>
              </div>
              {expandedSections.has('matrix') ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>

            {expandedSections.has('matrix') && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Skill
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Current
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Required
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Gap
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Priority
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Time
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Impact
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {analysis.skills_gap_matrix.map((skill, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {skill.skill}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{skill.current_level}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{skill.required_level}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={cn('font-medium', getGapSizeColor(skill.gap_size))}>
                            {skill.gap_size}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={cn(
                              'px-2 py-1 rounded-full text-xs font-medium border',
                              getPriorityColor(skill.priority)
                            )}
                          >
                            {skill.priority}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {skill.time_to_acquire}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{skill.career_impact}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Priority Skills Development */}
        {analysis.priority_skills_development &&
          analysis.priority_skills_development.length > 0 && (
            <div className="mb-6">
              <button
                onClick={() => toggleSection('priority')}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors mb-4"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500 rounded-lg text-white">
                    <TrendingUp className="h-5 w-5" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">Priority Skills Development</h3>
                    <p className="text-sm text-gray-600">
                      {analysis.priority_skills_development.length} priority skills with ROI
                      analysis
                    </p>
                  </div>
                </div>
                {expandedSections.has('priority') ? (
                  <ChevronUp className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                )}
              </button>

              {expandedSections.has('priority') && (
                <div className="space-y-4">
                  {analysis.priority_skills_development.map((skill, idx) => (
                    <div
                      key={idx}
                      className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="text-lg font-semibold text-gray-900">
                          {idx + 1}. {skill.skill_name}
                        </h4>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                          Priority {idx + 1}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                            Current State
                          </p>
                          <p className="text-sm text-gray-700">{skill.current_state}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                            Target State
                          </p>
                          <p className="text-sm text-gray-700">{skill.target_state}</p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                          Why High Priority
                        </p>
                        <p className="text-sm text-gray-700">{skill.why_priority}</p>
                      </div>

                      {skill.roi && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <DollarSign className="h-5 w-5 text-green-600" />
                            <h5 className="font-semibold text-green-900">ROI Analysis</h5>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="font-medium text-green-900">Investment</p>
                              <p className="text-green-800">{skill.roi.investment}</p>
                            </div>
                            <div>
                              <p className="font-medium text-green-900">Return</p>
                              <p className="text-green-800">{skill.roi.return}</p>
                            </div>
                            <div>
                              <p className="font-medium text-green-900">Payback Period</p>
                              <p className="text-green-800">{skill.roi.payback_period}</p>
                            </div>
                            <div>
                              <p className="font-medium text-green-900">Risk Level</p>
                              <p className="text-green-800">{skill.roi.risk_level}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        {/* Development Timeline */}
        {analysis.development_timeline && (
          <div className="mb-6">
            <button
              onClick={() => toggleSection('timeline')}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors mb-4"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500 rounded-lg text-white">
                  <Calendar className="h-5 w-5" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">30-60-90 Day Development Plan</h3>
                  <p className="text-sm text-gray-600">Structured learning roadmap</p>
                </div>
              </div>
              {expandedSections.has('timeline') ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>

            {expandedSections.has('timeline') && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {analysis.development_timeline.days_1_30 && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="h-5 w-5 text-blue-600" />
                      <h4 className="font-semibold text-blue-900">Days 1-30</h4>
                    </div>
                    <p className="text-sm text-blue-800 mb-2">
                      <span className="font-medium">Focus:</span>{' '}
                      {analysis.development_timeline.days_1_30.skills_focus}
                    </p>
                    {analysis.development_timeline.days_1_30.key_activities?.length > 0 && (
                      <div className="text-sm text-blue-700 space-y-1">
                        {analysis.development_timeline.days_1_30.key_activities.map(
                          (activity: string, idx: number) => (
                            <p key={idx}>• {activity}</p>
                          )
                        )}
                      </div>
                    )}
                  </div>
                )}

                {analysis.development_timeline.days_31_60 && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <h4 className="font-semibold text-green-900">Days 31-60</h4>
                    </div>
                    <p className="text-sm text-green-800 mb-2">
                      <span className="font-medium">Focus:</span>{' '}
                      {analysis.development_timeline.days_31_60.skills_focus}
                    </p>
                    {analysis.development_timeline.days_31_60.key_activities?.length > 0 && (
                      <div className="text-sm text-green-700 space-y-1">
                        {analysis.development_timeline.days_31_60.key_activities.map(
                          (activity: string, idx: number) => (
                            <p key={idx}>• {activity}</p>
                          )
                        )}
                      </div>
                    )}
                  </div>
                )}

                {analysis.development_timeline.days_61_90 && (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Award className="h-5 w-5 text-orange-600" />
                      <h4 className="font-semibold text-orange-900">Days 61-90</h4>
                    </div>
                    <p className="text-sm text-orange-800 mb-2">
                      <span className="font-medium">Focus:</span>{' '}
                      {analysis.development_timeline.days_61_90.skills_focus}
                    </p>
                    {analysis.development_timeline.days_61_90.key_activities?.length > 0 && (
                      <div className="text-sm text-orange-700 space-y-1">
                        {analysis.development_timeline.days_61_90.key_activities.map(
                          (activity: string, idx: number) => (
                            <p key={idx}>• {activity}</p>
                          )
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ROI Summary */}
        {analysis.roi_summary && (
          <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">ROI Summary</h3>
                <p className="text-sm text-gray-600">Investment vs Expected Returns</p>
              </div>
            </div>

            {analysis.roi_summary.overall_roi && (
              <div className="mb-4 p-4 bg-white rounded-lg border border-green-200">
                <p className="text-2xl font-bold text-green-600">
                  {analysis.roi_summary.overall_roi}
                </p>
                <p className="text-sm text-gray-600">Overall Return on Investment</p>
              </div>
            )}

            {analysis.roi_summary.strategic_recommendations?.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Strategic Recommendations</h4>
                <div className="space-y-2">
                  {analysis.roi_summary.strategic_recommendations.map(
                    (rec: string, idx: number) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-gray-700">{rec}</p>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillsGapAnalysis;
